// ── useColorMode ─────────────────────────────────────────────────────
//
// React binding for the framework-free `helpers/colorMode.js` store.
// Subscribes via `useSyncExternalStore` so any component re-renders
// when the preference changes (toggle in the topbar) OR when the OS
// scheme flips while the preference is "system".
//
// Returns:
//   preference  "light" | "dark" | "system"  (what the user picked)
//   mode        "light" | "dark"             (what's actually applied)
//   setPreference(p)                          persist + apply a choice
//   toggle()                                  flip light <-> dark
//
// MUI's palette mode is driven off `mode` in `components/App.js`; the
// topbar avatar flyout drives `setPreference` / reads `preference`.

import { useSyncExternalStore } from "react";
import {
  getColorModeState,
  setColorModePreference,
  subscribeColorMode,
  toggleColorMode,
} from "./colorMode";

// Stable server snapshot for SSR / pre-hydration (CRA renders client
// side, but `useSyncExternalStore` still asks for one).
const SERVER_SNAPSHOT = { preference: "system", mode: "light" };

// Cache the snapshot object so `getSnapshot` returns a referentially
// stable value between notifications — `useSyncExternalStore` bails
// with an infinite loop if a fresh object is returned every call.
let cachedSnapshot = getColorModeState();
const getSnapshot = () => {
  const next = getColorModeState();
  if (
    next.preference !== cachedSnapshot.preference ||
    next.mode !== cachedSnapshot.mode
  ) {
    cachedSnapshot = next;
  }
  return cachedSnapshot;
};

const useColorMode = () => {
  const { preference, mode } = useSyncExternalStore(
    subscribeColorMode,
    getSnapshot,
    () => SERVER_SNAPSHOT
  );

  return {
    preference,
    mode,
    setPreference: setColorModePreference,
    toggle: toggleColorMode,
  };
};

export default useColorMode;
